import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observabilityApi, type OperationsDashboard } from '../../api/observability';

const NAV = [
  { to: '/observability/health', label: 'Health Dashboard' },
  { to: '/observability/metrics', label: 'Metrics Explorer' },
  { to: '/observability/logs', label: 'Log Explorer' },
  { to: '/observability/traces', label: 'Trace Viewer' },
  { to: '/observability/incidents', label: 'Incident Center' },
  { to: '/observability/alerts', label: 'Alert Manager' },
  { to: '/observability/runbooks', label: 'Runbook Center' },
  { to: '/observability/capacity', label: 'Capacity Dashboard' },
  { to: '/observability/performance', label: 'Performance Dashboard' },
];

export function OperationsCenterPage() {
  const [dashboard, setDashboard] = useState<OperationsDashboard | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void observabilityApi.dashboard().then(setDashboard).catch((e) => setError(String(e)));
  }, []);

  const stats = dashboard
    ? [
        { label: 'Overall Health', value: dashboard.health.overall },
        { label: 'Metrics', value: dashboard.total_metrics },
        { label: 'Traces', value: dashboard.total_traces },
        { label: 'Logs', value: dashboard.total_logs },
        { label: 'Active Alerts', value: dashboard.active_alerts },
        { label: 'Open Incidents', value: dashboard.open_incidents },
        { label: 'Runbooks', value: dashboard.runbook_count },
        { label: 'SLO Compliance', value: `${dashboard.slo_compliance_pct.toFixed(1)}%` },
      ]
    : [];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Operations Center</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Enterprise observability, monitoring, telemetry, distributed tracing and SRE platform.
      </p>
      <nav className="mt-4 flex flex-wrap gap-3 text-sm">
        {NAV.map((l) => (
          <Link key={l.to} to={l.to} className="text-slate-700 hover:underline dark:text-slate-300">{l.label}</Link>
        ))}
      </nav>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-sm text-slate-500">{s.label}</p>
            <p className="mt-1 text-2xl font-semibold capitalize">{String(s.value ?? 0)}</p>
          </div>
        ))}
      </div>
      {dashboard?.platform_health && dashboard.platform_health.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium">Platform Integrations</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {dashboard.platform_health.map((p) => (
              <div key={p.module} className="rounded-lg border px-3 py-2 text-sm dark:border-slate-700">
                <span className="font-medium">{p.module}</span>
                <span className={`ml-2 capitalize ${p.status === 'healthy' ? 'text-green-600' : p.status === 'degraded' ? 'text-yellow-600' : 'text-red-600'}`}>
                  {p.status}
                </span>
                {p.latency_ms > 0 && <span className="ml-2 text-slate-500">{p.latency_ms}ms</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
